import {
  useCallback, useEffect, useMemo, useRef, useState,
  type FormEvent, type ReactNode,
} from 'react'
import {
  api, ApiError,
  type BroadcastPreview,
  type CallQuestion, type ListItem, type NatureItem,
  type NatureQuestions, type QuestionInput, type QuestionKind, type QuestionSet,
  type QuestionSetDetail,
} from '../lib/api'
import { findNatures } from '../lib/natures'
import { useDialogGuard, useReturnFocus } from '../lib/sheet'
import { decodeVehicle, vehicleText } from '../lib/vehicles'
import {
  QuestionFields, alerting, answerText, broadcastLine, callCode, noteFor, outstanding, prune,
  underStaying, visible,
} from '../lib/questions'

/** Every nature of call, and the questions each one asks. ONE TAB.
 *
 *  THE CUSTOMER WROTE THIS FEATURE OUT HIMSELF: "The system should work as a
 *  conditional decision tree. The dispatcher first selects the Nature of Call,
 *  and only the questions relevant to that call type and previous answers
 *  should appear." And: "I have much more natures... So I should be able to add
 *  the nature and add all those questions till it gets final to release to the
 *  broadcast."
 *
 *  AND THEN HE COULD NOT FIND IT: "Nature of calls. Call questions I think
 *  should be under one tab. And when we click on the nature, you should have an
 *  option of adding the questions and building up the questions. Now I think
 *  your questions are globally. It's still too hard for me to figure out what
 *  you built." He was right and it was a real design fault. The natures were a
 *  list on one tab and their questions a tree on another, so the questions read
 *  as a separate global thing rather than as something a nature HAS — and with
 *  seventy-odd natures entered, nothing on any screen said which of them asked
 *  anything at all.
 *
 *  SO THE NATURE LIST IS THE NAVIGATION. Left, the natures, filterable, each
 *  with the number of questions it asks; in the middle, the questions of
 *  whichever one is selected; right, a rehearsal of what a dispatcher would be
 *  asked. Clicking a nature is what opens its questions, which is his sentence.
 *
 *  THE THIRD COLUMN IS HIS TOO: "I will add a third column on the right side
 *  that we should be able to test it right away. We shouldn't need to go back
 *  and do, uh, assuming as the call comes in, and we are asking the questions.
 *  When we click on one, you should be able to test it and see the whole flow
 *  if it works fine." It runs `QuestionFields` and `visible()` — the
 *  dispatcher's own form and the browser's copy of the server's rule — and
 *  writes nothing. See `Rehearsal`.
 *
 *  THE SHARED SETS SIT UNDER THE NATURE LIST, NOT ON A TAB OF THEIR OWN. A set
 *  belongs to no nature — that is the whole point of it — so it is the one
 *  genuinely organization-wide thing here and it still needs a home. It is
 *  subordinate to the natures rather than beside them, because a second
 *  top-level list is exactly what made the questions look global.
 *
 *  SO THIS SCREEN HAS TO MAKE THE SHAPE VISIBLE. A flat form of eight boxes
 *  would let him build the tree and never let him READ it, and what he is
 *  actually maintaining is which question hangs off which answer. Every
 *  question is drawn under the one it depends on, with the condition in words,
 *  so a mistake looks like a mistake.
 *
 *  IT IS GATED ON `settings.lists` — THE TAB IS, AND THAT IS THE ONLY GATE
 *  DRAWN HERE. Not a new leaf: it already means "the organization's own lists",
 *  and BOTH halves of this screen check it on the server —
 *  `/api/settings/natures` for the list and `/api/questions/...` for the
 *  questions. Section 19 forbids a tick for a distinction the product draws
 *  nowhere, and merging the two tabs removed the last place it might have been
 *  drawn.
 *
 *  WHAT IT DELIBERATELY DOES NOT OFFER, because the server will not do it and a
 *  control drawn and then refused is worse than no control:
 *
 *  - RENAMING A QUESTION'S KEY. It is generated from the wording the first time
 *    the question is saved and then frozen: every answer already recorded is
 *    filed under it and every condition points at it, so a rename would detach
 *    the answers and stop the conditions, both silently. Rewording the PROMPT
 *    is safe and is what this screen offers.
 *  - CHANGING A QUESTION BETWEEN MULTIPLE-CHOICE AND TYPED. Its options, its
 *    safety answer and every condition naming one of those options would be
 *    left pointing at nothing.
 *  - DELETING ANYTHING. Questions and sets are RETIRED, and there is no delete
 *    route to call.
 *  - EDITING A SET'S QUESTION FROM A NATURE. It would edit it for every nature
 *    that includes the set, which is the one thing about sets he must not
 *    discover by accident. The button in the place he would try opens the set
 *    instead, and says so.
 */

// ---------------------------------------------------------------------------
// Drawing one question
// ---------------------------------------------------------------------------

/** The part of a question this screen draws.
 *
 *  A STRUCTURAL SHAPE RATHER THAN `CallQuestion`, so that the worked examples
 *  at the bottom — the customer's own three call types, written out as he sent
 *  them — go through the SAME renderer as the live tree. Anything else would be
 *  a second drawing of a question that could show his examples in a shape the
 *  editor does not produce, which is the one thing that comparison is for.
 *  `CallQuestion` satisfies it, so nothing converts. */
export type OutlineQuestion = {
  key: string
  prompt: string
  kind: QuestionKind
  options: string[] | null
  /** Optional so the worked examples need not invent any. */
  option_codes?: string[] | null
  ask_when_key: string | null
  ask_when_values: string[] | null
  alert_values: string[] | null
  raises_priority: boolean
  in_broadcast: boolean
  /** Optional so the worked examples need not say; absent reads as sent. */
  in_details?: boolean
  /** How the answer reads in the member's message (084); absent is none. */
  short_word?: string | null
  /** Optional, like `option_codes`, so the worked examples need not invent any. */
  option_notes?: string[] | null
  option_broadcasts?: string[] | null
  expect_answer?: boolean
  /** More than one answer may be picked (076). */
  multiple?: boolean
  /** Asked only when the caller is not staying on scene (077). */
  ask_when_caller_leaves?: boolean
}

/** How deep each question sits, by following its condition back up the list.
 *
 *  IT IS COMPUTED OVER THE WHOLE LIST IT IS DRAWN FROM, not per set: a nature's
 *  own question can hang off one that came out of a shared set, and a set's
 *  question can hang off one of the nature's own asked earlier. A condition
 *  naming something not in the list at all sits at the top level and is flagged
 *  separately — the server's `unreachable` is what decides that, per nature,
 *  and this is only indentation. */
function depths(questions: readonly OutlineQuestion[]): Record<string, number> {
  const depth: Record<string, number> = {}
  for (const question of questions) {
    const parent = question.ask_when_key
    depth[question.key] = parent !== null && parent in depth
      ? depth[parent] + 1
      // Asked only when the caller is leaving (077): conditional too.
      : question.ask_when_caller_leaves ? 1 : 0
  }
  return depth
}

function promptsByKey(
  questions: readonly OutlineQuestion[],
): Record<string, string> {
  const map: Record<string, string> = {}
  for (const question of questions) map[question.key] = question.prompt
  return map
}

/** One question in the outline: what it asks, what it offers, and what has to
 *  be true for it to be asked at all. */
function QuestionRow({
  question, depth, promptFor, problem, actions,
}: {
  question: OutlineQuestion
  depth: number
  promptFor: Record<string, string>
  /** Why this one can never be asked. Drawn in red on the row rather than only
   *  in a banner: the banner says how many, the row says which. */
  problem?: string | null
  actions?: ReactNode
}) {
  const condition = question.ask_when_key
  const values = question.ask_when_values ?? []
  return (
    <div
      className={`qnode ${problem ? 'bad' : ''}`}
      // CAPPED, because a chain six deep would walk the prompts off the right
      // of the card and the indentation is a hint rather than the record. The
      // condition line underneath is what actually says what it hangs off.
      style={{ marginLeft: Math.min(depth, 4) * 18 }}
    >
      <div className="qnode-head">
        <span className="qnode-prompt">{question.prompt}</span>
        {question.kind === 'text' && <span className="pill">typed</span>}
        {question.kind === 'vehicle' && (
          <span className="pill">make · model · year · colour</span>
        )}
        {(question.alert_values ?? []).length > 0 && (
          <span className="pill alarm">
            red on {(question.alert_values ?? []).map((v) => `“${v}”`).join(', ')}
            {question.raises_priority && ' · top of queue'}
          </span>
        )}
        {question.in_broadcast && <span className="pill wait">in the broadcast</span>}
        {/* THE EXCEPTION IS THE PILL: every answer goes to the acceptor
            unless this was unticked, so the tick's absence is the news. */}
        {question.in_details === false && <span className="pill">kept from the acceptor</span>}
        {question.in_details !== false && question.short_word && (
          <span className="pill" title="How the answer reads in the message to the member going">
            “{question.short_word}:”
          </span>
        )}
        {(question.option_broadcasts ?? []).some(Boolean) && (
          <span className="pill wait">some answers in the text</span>
        )}
        {question.expect_answer && <span className="pill">expects an answer</span>}
        {question.multiple && <span className="pill">more than one answer</span>}
        {actions && <span className="qnode-acts">{actions}</span>}
      </div>
      {question.ask_when_caller_leaves && (
        <div className="qcond">
          asked when <strong>the caller is not staying on scene</strong>
        </div>
      )}
      {condition !== null && (
        <div className="qcond">
          asked when <strong>{promptFor[condition] ?? condition}</strong> is
          {' '}{values.join(' or ')}
        </div>
      )}
      {question.kind === 'choice' && (
        <div className="qopts">
          {/* EACH PIECE BESIDE ITS OWN ANSWER, so which answer adds which part
              of the call code can be read down the tree without opening a
              question -- that pairing is the thing to check. */}
          {(question.options ?? []).map((option, i) => (
            <span key={option}>
              {i > 0 && ' · '}
              {option}
              {question.option_codes?.[i] && (
                <b className="qpiece">{question.option_codes[i]}</b>
              )}
              {/* WHAT GOES WITH THIS ANSWER, readable down the tree without
                  opening a question: a note for the responder, and the line the
                  text adds. */}
              {question.option_notes?.[i] && (
                <span className="qoptnote" title={question.option_notes[i]}> ✎ note</span>
              )}
              {question.option_broadcasts?.[i] && (
                <span className="qoptcast"> → “{question.option_broadcasts[i]}”</span>
              )}
            </span>
          ))}
        </div>
      )}
      {problem && <div className="qbad">{problem}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

/** What the right pane is showing.
 *
 *  ONE SELECTION ACROSS BOTH LISTS RATHER THAN ONE EACH. Two independent picks
 *  would leave a nature highlighted on the left while a set filled the pane, so
 *  nothing on screen would say which of the two the questions belonged to —
 *  and "I think your questions are globally" is precisely that confusion. */
type Pick =
  | { kind: 'nature'; id: string }
  | { kind: 'set'; id: string }

export function CallQuestions() {
  const [natures, setNatures] = useState<ListItem[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [sets, setSets] = useState<QuestionSet[]>([])
  const [pick, setPick] = useState<Pick | null>(null)
  const [filter, setFilter] = useState('')
  const [showRetired, setShowRetired] = useState(false)
  const [newNature, setNewNature] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** The tree the third column rehearses, handed up by the editor.
   *
   *  IT IS THE EDITOR'S OWN COPY AND NOT A SECOND FETCH, which is what stops
   *  the rehearsal going stale: every write in `NatureEditor` answers with the
   *  whole picture and stores it, so the rehearsal redraws from the same object
   *  the middle column is drawing, in the same render. A pane that fetched for
   *  itself would be showing the tree as it was when it last asked.
   *
   *  IT CARRIES THE NATURE'S ID because a tree without one is a tree that can
   *  be drawn under the wrong heading. Switching natures remounts the editor
   *  and its `view` starts null, so for one frame the id here is the new nature
   *  and the questions would be the old one's — the render below compares the
   *  two rather than trusting the order effects happen to run in. */
  const [rehearsal, setRehearsal] = useState<
    { natureId: string; tree: CallQuestion[] } | null>(null)

  /** Open something, and drop the rehearsal that belonged to the last thing.
   *
   *  IT IS THE ONE WAY THE PANE CAN GO STALE. Everything else redraws it: the
   *  editor hands its tree up on every write. But leaving a nature — into a
   *  shared set, whose questions that nature ASKS — and coming back would draw
   *  the tree as it was before that set was edited, for the moment between the
   *  editor remounting and its fetch answering. That is the one drift this
   *  column exists to catch, showing up in the column itself. */
  function choose(next: Pick) {
    // ONLY WHEN IT IS ACTUALLY A DIFFERENT THING. Clearing unconditionally
    // meant that clicking the row you are ALREADY on -- an ordinary gesture
    // while scanning seventy of them -- blanked the column for good: the
    // editor does not remount for the same `nature.id`, so nothing ever
    // handed the tree back, and the pane stayed empty until a write landed.
    //
    // Which is worse than it sounds, because an absent column already MEANS
    // something: "this nature asks nothing yet". So a stray second click told
    // him a nature asks nothing, about a nature asking four things, from the
    // one column whose whole job is saying whether the tree works.
    if (pick?.kind !== next.kind || pick.id !== next.id) setRehearsal(null)
    setPick(next)
  }

  /** RETIRED NATURES ARE ALWAYS FETCHED AND ONLY THE LIST HIDES THEM. Retiring
   *  the nature that is open would otherwise take it off the list and empty the
   *  pane in the same instant, putting Restore behind a checkbox somebody has
   *  to find — for something they did one click ago.
   *
   *  THE SETS FOLLOW THE CHECKBOX INSTEAD, and that is not an inconsistency:
   *  there are a handful of them and the tick is the only way back to a retired
   *  one. It used to be a dead end — the list carried the live sets, so
   *  retiring a set and then clicking anything else left it findable by
   *  nothing. Retired or not, a set that is already on a nature stays on it,
   *  which is the whole asymmetry of retiring rather than deleting. */
  const loadLists = useCallback(async () => {
    try {
      const [all, howMany, bundles] = await Promise.all([
        api.settingsList('natures', true),
        api.questionNatureCounts(),
        api.questionSets(showRetired),
      ])
      setNatures(all)
      setCounts(Object.fromEntries(howMany.map((r) => [r.nature_id, r.questions])))
      setSets(bundles)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the questions.')
    }
  }, [showRetired])

  useEffect(() => { loadLists() }, [loadLists])

  /** The natures on screen. FILTERED IN THE BROWSER, which is honest here only
   *  because the list in the browser IS the whole list: `/api/settings/natures`
   *  is not paged, so there is no second page for a term to miss. He has
   *  seventy-odd of these and a wall of them is unreadable. A code typed here
   *  puts its nature first, by the same rule as the intake picker. */
  const shown = useMemo(() => findNatures(
    natures.filter((n) => showRetired || n.retired_at === null), filter,
  ), [natures, filter, showRetired])

  async function addNature(e: FormEvent) {
    e.preventDefault()
    const typed = newNature.trim()
    if (!typed) return
    setBusy(true)
    try {
      const made = await api.settingsCreate('natures', {
        name: typed, sort_order: natures.length,
      })
      // Cleared INSIDE the write: a duplicate name comes back naming it, and
      // the words have to still be in the box to be corrected.
      setNewNature('')
      await loadLists()
      // OPENED, because its questions are the next thing he wants: "I should be
      // able to add the nature and add all those questions till it gets final
      // to release to the broadcast."
      choose({ kind: 'nature', id: made.id })
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that.')
    } finally {
      setBusy(false)
    }
  }


  const nature = pick?.kind === 'nature'
    ? natures.find((n) => n.id === pick.id) ?? null
    : null

  /** Whether the third column is drawn. See the comment where it is. */
  const trying = !!nature && !nature.retired_at && !!rehearsal
    && rehearsal.natureId === nature.id
    && (visible(rehearsal.tree, {}).length > 0
      || visible(rehearsal.tree, {}, { callerLeaving: true }).length > 0)

  return (
    <div className="qpage">
      <aside className="qside">
        <form className="addrow" onSubmit={addNature}>
          <input
            value={newNature}
            maxLength={120}
            placeholder="Add a nature of call…"
            aria-label="Add a nature of call"
            onChange={(e) => setNewNature(e.target.value)}
          />
          <button className="primary compact" disabled={busy || !newNature.trim()}>
            Add
          </button>
        </form>

        <div className="callbar">
          <input
            className="grow"
            value={filter}
            maxLength={120}
            placeholder="Filter by name or code…"
            aria-label="Filter the natures of call by name or code"
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="check inline">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired
          </label>
        </div>

        {error && <div className="notice" role="alert">{error}</div>}

        {/* A HEADING THE COUNT CAN BELONG TO. The number on each row was a
            bare digit with its meaning only in an `aria-label` -- so a sighted
            reader scanning seventy rows of "Flat Tire 6 / Boost 5 / Towing 0"
            had no word on screen to attach it to, and it reads equally well as
            a sort position or an id. That number answers the one question this
            merge was made to answer. */}
        <div className="qnathead">
          {/* BEFORE THE NAME, his placement: "a column maybe even before the
              nature of calls, not in the middle". */}
          <span className="lbl qcode">Code</span>
          <span className="lbl nm">Nature of call</span>
          <span className="lbl">Questions</span>
        </div>
        <div className="qsidelist">
          {shown.length === 0 ? (
            <div className="qempty">
              {/* THREE STATES, NOT TWO. `natures` holds RETIRED rows now
                  -- deliberately, so retiring the open one does not empty the
                  pane -- so "not empty" no longer means "something is
                  showing". With every nature retired and no filter typed, this
                  blamed a search term nobody had entered, and the way back is
                  the tick it does not mention. */}
              {natures.length === 0
                ? 'No natures of call yet. Add the ones your dispatchers use.'
                : filter.trim()
                  ? 'Nothing matches that.'
                  : 'Every nature is retired. Tick Show retired to see them.'}
            </div>
          ) : shown.map((n) => {
            const asked = counts[n.id] ?? 0
            const open = pick?.kind === 'nature' && pick.id === n.id
            return (
              <button
                key={n.id}
                type="button"
                className={`qrow ${open ? 'on' : ''} ${n.retired_at ? 'retired' : ''}`}
                // THE COUNT IS A BARE DIGIT ON SCREEN — see `.qcount` in
                // theme.css for why — so it is spelled out here instead, where
                // it costs nothing and is not repeated down seventy rows.
                aria-label={(n.code ? `${n.code}, ` : '') + (n.retired_at
                  ? `${n.name}, retired`
                  : `${n.name}, ${asked} question${asked === 1 ? '' : 's'}`)}
                onClick={() => choose({ kind: 'nature', id: n.id })}
              >
                {/* An empty cell for a nature with no code, so every name
                    starts under the same heading. */}
                <span className="qcode" aria-hidden="true">{n.code}</span>
                <span className="nm">{n.name}</span>
                {n.retired_at
                  // NO COUNT ON A RETIRED ONE, because there is none to show:
                  // `/api/questions/natures` answers for LIVE natures only, and
                  // printing 0 beside a nature that still holds its questions
                  // would be a figure this screen invented.
                  ? <span className="tag">retired</span>
                  : (
                    <span className={`qcount ${asked === 0 ? 'none' : ''}`}
                          aria-hidden="true">
                      {asked}
                    </span>
                  )}
              </button>
            )
          })}
        </div>

        {/* THE SETS ARE NOT ON THE FRONT OF THIS SCREEN, and that is his
            decision after seeing them there.

            "I don't like that you do share questions because not all natures
             are related to a car. So now how do I add questions, let's say, to
             a child locked in a house?"

            The concept was right -- he asked for the road questions as a
            reusable component in the first place -- but a heading saying
            SHARED QUESTION SETS above the natures made sharing look like the
            way in, when it is the exception. Most of seventy-odd natures share
            nothing: House break-in, Suspicious package and Child locked in
            each want their own questions and no set will ever apply to them.

            So a nature's own questions ARE the screen, and a set is reached
            from inside a nature, by the one control that wants one -- "Add a
            shared set" in the editor. Somebody who never presses it never
            meets the idea. */}

      </aside>

      {/* THE EDITOR AND THE REHEARSAL SHARE A WRAPPER so that a narrow window
          stacks the rehearsal UNDER the editor rather than squeezing three
          columns into a width none of them can be read at. Without it the two
          would go under the nature list instead, which puts the questions
          somebody is editing below a scroll.

          `reserve` HOLDS THE THIRD COLUMN'S ROOM WHILE A NATURE IS OPEN AND
          NOT BEING REHEARSED -- see `.qwork.reserve` in theme.css for why. Only
          while a nature is open: with nothing open, or a shared set, there is
          no rehearsal to make room for, and the room held anyway was a blank
          band down the right of the screen. */}
      <div className={`qwork ${nature && !trying ? 'reserve' : ''}`}>
        <div className="qmain">
          {pick?.kind === 'set' ? (
            // BY ID AND NOT OFF THE LIST, so a set stays open in the pane after
            // it is retired: `questionSets` answers with the live ones, and an
            // editor that vanished at the moment of retiring would put Restore
            // out of reach. `SetEditor` fetches its own detail and handles the
            // retired state itself.
            <SetEditor key={pick.id} setId={pick.id} onChanged={loadLists} />
          ) : nature ? (
            <NatureEditor
              key={nature.id}
              nature={nature}
              sets={sets}
              onChanged={loadLists}
              onTree={setRehearsal}
              onOpenSet={(id) => choose({ kind: 'set', id })}
            />
          ) : (
            /* THE WORKED EXAMPLES EARN THE PANE ONLY WHILE IT IS EMPTY. They
               used to be a permanent card competing with the organization's own
               data, on a screen he had already called too large; here they are
               what is on screen at the one moment he has nothing open and may
               not know what the shape is meant to look like. */
            <>
              <div className="card">
                <h2>Questions</h2>
                <div className="listempty">
                  Pick a nature of call on the left; its questions open here.
                </div>
              </div>
              <WorkedExamples />
            </>
          )}
        </div>

        {/* NOTHING SELECTED IS NOTHING TO REHEARSE, and neither is a nature
            that asks nothing yet — a heading over an empty frame is a column
            saying "this is broken" while he is building the first question.

            A RETIRED NATURE IS NOT REHEARSED EITHER. `Intake.tsx` filters
            retired natures out of the dispatcher's list, so not one of its
            questions is ever asked on a real call — and this column answers
            "does the whole flow work". Answering yes about a flow nobody can
            reach is the exact direction of error it was built to prevent, and
            the moment it would happen is the worst one: he retires a nature,
            the editor deliberately keeps it open so he can undo that, and the
            rehearsal beside it goes on demonstrating a healthy tree.

            AND THE TEST IS WHAT THE DISPATCHER WOULD SEE, not what the tree
            contains. `visible(tree, {})` is the same function the pane draws
            through: a nature whose questions ALL hang off a key nothing asks
            -- one question left behind by a removed shared set is enough --
            has a non-empty tree and shows no fields at all. Guarded on the
            length alone, the column drew its heading, "Nothing answered yet",
            and a green "all answered" pill, beside the middle column's red
            banner saying those questions can never be asked. Telling him the
            tree is fine at the exact moment it is broken is the one failure
            this column cannot be allowed to have. */}
        {trying && nature && rehearsal && (
          /* THE ID TRAVELS WITH THE TREE IT BELONGS TO. Both come off the one
             `rehearsal` object rather than one from here and one from
             `nature`, so the broadcast preview can never be composed for a
             different nature than the questions drawn above it. The code is
             the nature's own, and `trying` has already checked that nature is
             the one the tree belongs to. */
          <Rehearsal
            key={nature.id}
            natureId={rehearsal.natureId}
            natureCode={nature.code ?? null}
            tree={rehearsal.tree}
          />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One nature's sequence
// ---------------------------------------------------------------------------

function NatureEditor({
  nature, sets, onChanged, onTree, onOpenSet,
}: {
  nature: ListItem
  sets: QuestionSet[]
  onChanged: () => void
  /** Hand the resolved tree to the rehearsal column, every time it changes.
   *
   *  THIS IS WHAT KEEPS THE REHEARSAL FROM GOING STALE. Every write here
   *  answers with the whole picture and stores it, so one edit in this column
   *  redraws the one beside it in the same render — a rehearsal showing
   *  yesterday's tree is the drift the whole feature exists to catch, one step
   *  removed. Null while nothing is loaded. */
  onTree: (held: { natureId: string; tree: CallQuestion[] } | null) => void
  /** Show a shared set in this pane. What the buttons on a set's questions do
   *  instead of editing them where they stand. */
  onOpenSet: (setId: string) => void
}) {
  const [view, setView] = useState<NatureQuestions | null>(null)
  const [name, setName] = useState(nature.name)
  /** The code as typed, in capitals as it is typed -- the server stores
   *  capitals, so the box shows what will be stored rather than changing it
   *  after the save. */
  const [code, setCode] = useState(nature.code ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<CallQuestion | null>(null)
  const [pickSet, setPickSet] = useState('')

  const load = useCallback(async () => {
    try {
      setView(await api.questionNature(nature.id))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load this nature.')
    }
  }, [nature.id])

  useEffect(() => { load() }, [load])

  // `view.tree` is the SAME value `GET /api/questions/natures/{id}/tree` hands
  // the intake form -- both are `call_questions.tree_for`, sets expanded,
  // retired questions out, `sort_order` resolved. So the rehearsal beside this
  // column is asked what a dispatcher would be asked, and not an approximation
  // of it assembled here.
  useEffect(() => {
    onTree(view ? { natureId: nature.id, tree: view.tree } : null)
  }, [view, nature.id, onTree])

  /** Every write answers with the whole picture, so this stores what came back
   *  rather than re-reading. A screen that re-requests after each change is a
   *  screen that can draw an order the server does not have — which is exactly
   *  where it would be noticed, because somebody is moving rows. */
  function took(next: NatureQuestions) {
    setView(next)
    setError(null)
    onChanged()
  }

  async function run(work: () => Promise<NatureQuestions>) {
    if (busy) return
    setBusy(true)
    try {
      took(await work())
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  /** A write against the nature ITSELF — the settings list rather than the
   *  questions — so it answers with a `ListItem` and the parent's list is what
   *  has to be reloaded.
   *
   *  RENAMING, RECODING AND RETIRING THE NATURE LIVE HERE, and there is nowhere
   *  else left for them: the list on the left is seventy rows of a code, a name
   *  and a number, and hanging buttons off each of them is the wall this screen
   *  was merged to get rid of. Same route and same `settings.lists` key as the list editor
   *  that used to own them.
   *
   *  BOTH ARE SAFE ON HISTORY. An incident freezes the label it was closed with
   *  (`incidents.nature_label`), and nothing here deletes: a retired nature
   *  keeps its questions and brings them back with it. */
  async function saveList(work: () => Promise<unknown>) {
    if (busy) return
    setBusy(true)
    try {
      await work()
      onChanged()
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const typedName = name.trim()
  const typedCode = code.trim()
  const renamed = typedName !== nature.name
  const recoded = typedCode !== (nature.code ?? '')

  if (!view) {
    return (
      <div className="card">
        <h2>{nature.name}</h2>
        {error ? <div className="notice" role="alert">{error}</div>
          : <div className="listempty">Loading…</div>}
      </div>
    )
  }

  const depth = depths(view.tree)
  const promptFor = promptsByKey(view.tree)
  const byId = new Map(view.tree.map((q) => [q.id, q]))
  const unreachable = new Set(view.unreachable.map((q) => q.key))
  const order = view.items.map((i) => i.id)
  const spare = sets.filter(
    (s) => !s.retired_at && !view.items.some((i) => i.set_id === s.id))

  /** Which questions would stop being asked if these keys went, so the
   *  confirmation can name them.
   *
   *  RETIRING A QUESTION THAT SOMETHING HANGS OFF BREAKS THE TREE SILENTLY: the
   *  dependent stops appearing on every call, for ever, with nothing on the
   *  dispatcher's screen to say so. The server's `unreachable` catches it after
   *  the fact and this catches it before. */
  function dependents(going: Set<string>): CallQuestion[] {
    return view!.tree.filter((q) =>
      q.ask_when_key !== null
      && going.has(q.ask_when_key)
      && !going.has(q.key))
  }

  function move(item: NatureItem, by: number) {
    const from = order.indexOf(item.id)
    const to = from + by
    if (to < 0 || to >= order.length) return
    const next = [...order]
    next.splice(to, 0, ...next.splice(from, 1))
    run(() => api.questionNatureOrder(nature.id, next))
  }

  function remove(item: NatureItem) {
    const going = new Set<string>(
      item.set_id
        ? view!.tree.filter((q) => q.from_set_id === item.set_id).map((q) => q.key)
        : item.key ? [item.key] : [])
    const orphans = dependents(going)
    const what = item.set_id
      ? `Take “${item.set_name}” off ${nature.name}?`
        + ' The set itself is untouched and stays on every other nature asking it.'
      : `Retire “${item.prompt}”?`
        + ' It is kept, never deleted, so every answer already recorded under it'
        + ' still reads correctly — and it can be restored here.'
    const also = orphans.length
      ? `\n\nThis also stops ${orphans.length} question(s) from ever being`
        + ` asked on ${nature.name}, because they hang off an answer it would no`
        + ` longer collect:\n\n· ${orphans.map((q) => q.prompt).join('\n· ')}`
      : ''
    if (!confirm(what + also)) return
    run(async () => await api.questionNatureRemove(nature.id, item.id))
  }

  return (
    <div className="card qbuild">
      <h2>
        {nature.name}
        <span className="lbl">{view.tree.length} questions asked</span>
        {nature.retired_at && <span className="pill">retired</span>}
      </h2>

      {error && <div className="notice" role="alert">{error}</div>}

      <form
        className="callbar"
        onSubmit={(e) => {
          e.preventDefault()
          if (!typedName || (!renamed && !recoded)) return
          // ONLY WHAT CHANGED IS SENT. A `code` key is a write of the code,
          // null included, so sending it with every rename would write back
          // the code this screen loaded -- over one another administrator
          // has changed since.
          saveList(() => api.settingsUpdate('natures', nature.id, {
            ...(renamed ? { name: typedName } : {}),
            ...(recoded ? { code: typedCode || null } : {}),
          }))
        }}
      >
        {/* THE CODE BEFORE THE NAME, as on the list. Six at most, mirroring
            `call_questions.CODE_MAX`; anything else about its shape is the
            server's sentence to say, beside this box. */}
        <input
          className="qcodein"
          value={code}
          maxLength={6}
          placeholder="Code"
          aria-label="Code for this nature of call"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
        <input
          className="grow"
          value={name}
          maxLength={120}
          aria-label="Name of this nature of call"
          onChange={(e) => setName(e.target.value)}
        />
        <button className="ghost tiny nogrow" type="submit"
          disabled={busy || !typedName || (!renamed && !recoded)}>
          Save
        </button>
        <button
          className="ghost tiny nogrow"
          type="button"
          disabled={busy}
          onClick={() => {
            const going = nature.retired_at === null
            if (going && !confirm(
              `Retire “${nature.name}”? It comes off the dispatcher's intake`
              + ' list. Its questions are kept and come back with it, and every'
              + ' incident already closed under it still reads correctly.')) return
            saveList(
              () => api.settingsUpdate('natures', nature.id, { retired: going }))
          }}
        >
          {nature.retired_at ? 'Restore' : 'Retire'}
        </button>
      </form>

      {/* THE ONE FAILURE THIS EDITOR EXISTS TO CATCH. A condition names a
          question's key, and the database can only check that the key exists
          SOMEWHERE — not that this nature asks it EARLIER. When it does not,
          the question never appears, on every call, with nothing on the
          dispatcher's screen to say so. */}
      {view.unreachable.length > 0 && (
        <div className="notice" role="alert">
          {view.unreachable.length} question(s) below can never be asked on{' '}
          {nature.name}: the answer each one waits for is not collected earlier
          in this list. Add the question or the set it depends on, move it
          higher, or change the condition.
        </div>
      )}

      {view.items.length === 0 ? (
        <div className="listempty">
          Nothing asked yet. Add a question of its own, or drop in a shared set.
        </div>
      ) : (
        view.items.map((item, i) => {
          const controls = (
            <>
              <button className="ghost tiny nogrow" disabled={busy || i === 0}
                aria-label="Move up" onClick={() => move(item, -1)}>↑</button>
              <button className="ghost tiny nogrow"
                disabled={busy || i === view.items.length - 1}
                aria-label="Move down" onClick={() => move(item, 1)}>↓</button>
              <button className="ghost tiny nogrow" disabled={busy}
                onClick={() => remove(item)}>
                {item.set_id ? 'Take off' : 'Retire'}
              </button>
            </>
          )

          if (item.set_id) {
            const setId = item.set_id
            const inSet = view.tree.filter((q) => q.from_set_id === setId)
            return (
              <div className="qitemblock" key={item.id}>
                {/* WHAT THE LABEL SAYS IS THE CONCEPT HE HAS NOT GOT YET: these
                    questions are not this nature's, and changing one changes it
                    everywhere it is asked. It is said here, above the rows, and
                    again on the button somebody would reach for. */}
                <div className="qsethead">
                  <span className="nm">{item.set_name}</span>
                  <span className="lbl">
                    shared set · {inSet.length} question
                    {inSet.length === 1 ? '' : 's'} · edited once, for every
                    nature asking it
                  </span>
                  {item.set_retired_at && <span className="pill">retired</span>}
                  <span className="qnode-acts">
                    <button className="ghost tiny nogrow" disabled={busy}
                      onClick={() => onOpenSet(setId)}>
                      Open the set
                    </button>
                    {controls}
                  </span>
                </div>
                {inSet.length === 0 ? (
                  <div className="qsetempty">
                    {item.set_retired_at
                      ? 'This set has been retired, so it asks nothing here any more.'
                      : 'This set has no live questions, so it asks nothing here.'}
                  </div>
                ) : inSet.map((q) => (
                  <QuestionRow
                    key={q.key}
                    question={q}
                    depth={depth[q.key] ?? 0}
                    promptFor={promptFor}
                    problem={unreachable.has(q.key)
                      ? 'Never asked here — nothing earlier on this nature'
                        + ' collects the answer it waits for.'
                      : null}
                    actions={
                      /* NOT AN EDIT BUTTON, AND THAT IS THE RULE THIS SCREEN
                         HAS TO TEACH. The question belongs to the SET, so
                         rewording it from inside Flat tire rewords it for Boost
                         and Car lockout too — the one thing about sets he must
                         not find out by accident. An Edit here would open the
                         same dialog as a question of this nature's own and look
                         identical, and there is nothing after the save that
                         could tell him what he had just changed. So the button
                         in the place he would try says where the editing
                         happens, and takes him there. */
                      <button className="ghost tiny nogrow" disabled={busy}
                        onClick={() => onOpenSet(setId)}>
                        Edit in the set
                      </button>
                    }
                  />
                ))}
              </div>
            )
          }

          // ONE OF THIS NATURE'S OWN. A retired one keeps its row — that row IS
          // its position in this sequence, and deleting it would make a
          // restored question appear on no tree at all — so it is drawn struck
          // through with a way back rather than vanishing.
          const own = item.question_id ? byId.get(item.question_id) : undefined
          if (!own) {
            return (
              <div className="qitemblock" key={item.id}>
                <div className="qnode retired">
                  <div className="qnode-head">
                    <span className="qnode-prompt gone">{item.prompt}</span>
                    <span className="pill">retired</span>
                    <span className="qnode-acts">
                      <button
                        className="ghost tiny nogrow"
                        disabled={busy || !item.question_id}
                        onClick={() => run(async () => {
                          await api.questionUpdate(item.question_id!, { retired: false })
                          return api.questionNature(nature.id)
                        })}
                      >
                        Restore
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            )
          }
          return (
            <div className="qitemblock" key={item.id}>
              <QuestionRow
                question={own}
                depth={depth[own.key] ?? 0}
                promptFor={promptFor}
                problem={unreachable.has(own.key)
                  ? 'Never asked — nothing earlier on this nature collects the'
                    + ' answer it waits for.'
                  : null}
                actions={
                  <>
                    <button className="ghost tiny nogrow" disabled={busy}
                      onClick={() => setEditing(own)}>Edit</button>
                    {controls}
                  </>
                }
              />
            </div>
          )
        })
      )}

      <div className="callbar" style={{ marginTop: 12 }}>
        <button className="primary compact" disabled={busy}
          onClick={() => setAdding(true)}>
          Add a question
        </button>
        <select
          className="pick"
          value={pickSet}
          aria-label="Add a shared set"
          disabled={busy || spare.length === 0}
          onChange={(e) => {
            const chosen = e.target.value
            // CLEARED AFTER THE WRITE, NOT BEFORE IT. The responder picker on
            // the dispatch screen reset its select before the request went out,
            // so a refusal took the choice with it and left nothing on screen
            // saying what had been attempted.
            if (!chosen) return
            // MADE HERE, BECAUSE HERE IS WHERE SOMEBODY WANTS ONE. The sets
            // used to have their own list on the left, above the natures,
            // which is what made sharing look like the way in rather than the
            // exception -- his objection, and a fair one: most of seventy-odd
            // natures share nothing with anything.
            //
            // So the only door is this picker, on a nature. Somebody who never
            // opens it never meets the idea; somebody who does is already
            // looking at the questions he wants to reuse.
            if (chosen === '__new__') {
              const name = prompt(
                'Name the shared set.\n\n'
                + 'It is written once and included by as many natures as ask '
                + 'it — edit it later and every one of them changes with it.')
              setPickSet('')
              if (!name || !name.trim()) return
              run(async () => {
                const made = await api.questionSetCreate({ name: name.trim() })
                return await api.questionNatureAddSet(nature.id, made.id)
              })
              return
            }
            run(async () => {
              const done = await api.questionNatureAddSet(nature.id, chosen)
              setPickSet('')
              return done
            })
          }}
        >
          <option value="">Add a shared set…</option>
          {spare.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.questions})
            </option>
          ))}
          {/* LAST, AND ALWAYS THERE. With the sets list gone from the left
              column this is the only way to make one, and it has to be
              reachable on the FIRST nature somebody builds -- when there are
              no sets at all and the list above is empty. */}
          <option value="__new__">
            {spare.length === 0
              ? 'New shared set…'
              : '— or start a new shared set…'}
          </option>
        </select>
      </div>

      {adding && (
        <QuestionDialog
          title={`A question only ${nature.name} asks`}
          candidates={view.tree}
          onClose={() => setAdding(false)}
          onSave={async (body) => {
            const done = await api.questionNatureAdd(nature.id, body)
            took(done)
            setAdding(false)
          }}
        />
      )}

      {editing && (
        <QuestionDialog
          title="Edit a question"
          question={editing}
          // ONLY WHAT IS ASKED EARLIER ON THIS NATURE. A condition may name any
          // question in the organization as far as the database is concerned,
          // but one naming something asked later — or not asked here at all —
          // can never be true, which is what `unreachable` reports. Offering
          // only the earlier ones is the same rule, one step sooner.
          candidates={view.tree.filter((q) => q.sort_order < editing.sort_order)}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            await api.questionUpdate(editing.id, body)
            took(await api.questionNature(nature.id))
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// One shared set
// ---------------------------------------------------------------------------

function SetEditor({
  setId, onChanged,
}: {
  setId: string
  onChanged: () => void
}) {
  const [detail, setDetail] = useState<QuestionSetDetail | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<CallQuestion | null>(null)

  const load = useCallback(async () => {
    try {
      const got = await api.questionSet(setId, showRetired)
      setDetail(got)
      setName(got.name)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load that set.')
    }
  }, [setId, showRetired])

  useEffect(() => { load() }, [load])

  async function run(work: () => Promise<unknown>) {
    if (busy) return
    setBusy(true)
    try {
      await work()
      await load()
      onChanged()
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  if (!detail) {
    return (
      <div className="card">
        <h2>Question set</h2>
        {error ? <div className="notice" role="alert">{error}</div>
          : <div className="listempty">Loading…</div>}
      </div>
    )
  }

  const live = detail.questions.filter((q) => !q.retired_at)
  const depth = depths(live)
  const promptFor = promptsByKey(live)
  const order = live.map((q) => q.id)

  function move(question: CallQuestion, by: number) {
    const from = order.indexOf(question.id)
    const to = from + by
    if (to < 0 || to >= order.length) return
    const next = [...order]
    next.splice(to, 0, ...next.splice(from, 1))
    run(() => api.questionSetOrder(setId, next))
  }

  function retire(question: CallQuestion) {
    const orphans = live.filter((q) => q.ask_when_key === question.key)
    const used = detail!.natures.length
    if (!confirm(
      `Retire “${question.prompt}”?`
      + ' It is kept, never deleted, so every answer already recorded under it'
      + ' still reads correctly.'
      + (orphans.length
        ? `\n\n${orphans.length} question(s) in this set hang off its answer and`
          + ` would never be asked again:\n\n· `
          + orphans.map((q) => q.prompt).join('\n· ')
        : '')
      + (used
        ? `\n\nThis set is on ${used} nature(s). A question of theirs hanging off`
          + ' this answer will show as unreachable on that nature.'
        : ''))) return
    run(() => api.questionUpdate(question.id, { retired: true }))
  }

  return (
    <div className="card qbuild">
      <h2>
        {detail.name}
        <span className="lbl">
          {detail.natures.length === 0
            ? 'on no nature yet'
            : `on ${detail.natures.map((n) => n.name).join(', ')}`}
        </span>
      </h2>

      {error && <div className="notice" role="alert">{error}</div>}

      <form
        className="callbar"
        onSubmit={(e) => {
          e.preventDefault()
          const typed = name.trim()
          if (!typed || typed === detail.name) return
          run(() => api.questionSetUpdate(setId, { name: typed }))
        }}
      >
        <input
          className="grow"
          value={name}
          maxLength={120}
          aria-label="Name of this set"
          onChange={(e) => setName(e.target.value)}
        />
        <button className="ghost tiny nogrow" type="submit"
          disabled={busy || !name.trim() || name.trim() === detail.name}>
          Rename
        </button>
        <button
          className="ghost tiny nogrow"
          type="button"
          disabled={busy}
          onClick={() => {
            const going = !detail.retired_at
            if (going && detail.natures.length && !confirm(
              `Retire “${detail.name}”? ${detail.natures.length} nature(s) stop`
              + ' asking its questions at once:\n\n· '
              + detail.natures.map((n) => n.name).join('\n· ')
              + '\n\nEvery answer already recorded still reads correctly, and it'
              + ' can be restored here.')) return
            run(() => api.questionSetUpdate(setId, { retired: going }))
          }}
        >
          {detail.retired_at ? 'Restore the set' : 'Retire the set'}
        </button>
      </form>

      {detail.retired_at && (
        <p className="footnote">
          Retired. Natures already asking it keep it — that asymmetry is the
          whole point of retiring rather than deleting — but it cannot be added
          to anything new.
        </p>
      )}

      {detail.questions.length === 0 ? (
        <div className="listempty">
          Nothing in this set yet.
        </div>
      ) : (
        detail.questions.map((q) => (q.retired_at ? (
          <div className="qnode retired" key={q.id}>
            <div className="qnode-head">
              <span className="qnode-prompt gone">{q.prompt}</span>
              <span className="pill">retired</span>
              <span className="qnode-acts">
                <button className="ghost tiny nogrow" disabled={busy}
                  onClick={() => run(
                    () => api.questionUpdate(q.id, { retired: false }))}>
                  Restore
                </button>
              </span>
            </div>
          </div>
        ) : (
          <QuestionRow
            key={q.id}
            question={q}
            depth={depth[q.key] ?? 0}
            promptFor={promptFor}
            actions={
              <>
                <button className="ghost tiny nogrow" disabled={busy}
                  onClick={() => setEditing(q)}>Edit</button>
                <button className="ghost tiny nogrow"
                  disabled={busy || order.indexOf(q.id) === 0}
                  aria-label="Move up" onClick={() => move(q, -1)}>↑</button>
                <button className="ghost tiny nogrow"
                  disabled={busy || order.indexOf(q.id) === order.length - 1}
                  aria-label="Move down" onClick={() => move(q, 1)}>↓</button>
                <button className="ghost tiny nogrow" disabled={busy}
                  onClick={() => retire(q)}>Retire</button>
              </>
            }
          />
        )))
      )}

      <div className="callbar" style={{ marginTop: 12 }}>
        <button className="primary compact" disabled={busy}
          onClick={() => setAdding(true)}>
          Add a question to this set
        </button>
        <label className="check inline">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
          />
          Show retired
        </label>
      </div>

      {adding && (
        <QuestionDialog
          title={`A question in ${detail.name}`}
          candidates={live}
          onClose={() => setAdding(false)}
          onSave={async (body) => {
            await api.questionSetAdd(setId, body)
            await load()
            onChanged()
            setAdding(false)
          }}
        />
      )}

      {editing && (
        <QuestionDialog
          title="Edit a question"
          question={editing}
          candidates={live.filter((q) => q.id !== editing.id
            && order.indexOf(q.id) < order.indexOf(editing.id))}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            await api.questionUpdate(editing.id, body)
            await load()
            onChanged()
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Adding and editing one question
// ---------------------------------------------------------------------------

/** One answer he can choose, and the piece of the call code it adds -- F on
 *  "Front", nothing on "Not sure".
 *
 *  ONE ROW, NOT TWO LISTS: THIS IS WHERE THE PAIRING IS HELD IN THE BROWSER.
 *  The server keeps `options` and `option_codes` as parallel arrays paired by
 *  position, so a piece that slid one place would put F against "Back" and the
 *  code said on the radio would contradict the ticket, with nothing on screen
 *  to show it. Held as a row, renaming an answer or removing it cannot leave
 *  its piece behind; `save` splits the rows into the two arrays in one step. */
type Choice = {
  option: string
  piece: string
  /** One of the red answers. On the row, so renaming the answer keeps it red. */
  red: boolean
  /** A note for the responder when this answer is given. */
  note: string
  /** The line the text message adds when this answer is given. */
  text: string
}

const BLANK_CHOICE: Choice = { option: '', piece: '', red: false, note: '', text: '' }

/** "Only ask this when… the caller is not staying on scene" (077), held in the
 *  condition's own box; not a question key, which never starts with "@". */
const CALLER_LEAVES = '@caller_leaves'

type Draft = {
  prompt: string
  kind: QuestionKind
  choices: Choice[]
  ask_when_key: string
  ask_when_values: string[]
  raises_priority: boolean
  in_broadcast: boolean
  in_details: boolean
  /** How the answer reads in the member's message (084). */
  short_word: string
  expect_answer: boolean
  /** More than one answer may be picked (076): choice questions only. */
  multiple: boolean
}

/** What each kind is called on this dialog, in the words of its chip. */
const KIND_NAMES: Record<QuestionKind, string> = {
  choice: 'Pick one of a list',
  text: 'Typed in',
  vehicle: 'A vehicle',
}

function draftOf(question?: CallQuestion): Draft {
  return {
    prompt: question?.prompt ?? '',
    kind: question?.kind ?? 'choice',
    choices: question?.options
      ? question.options.map((option, i) => ({
        option,
        piece: question.option_codes?.[i] ?? '',
        red: (question.alert_values ?? []).includes(option),
        note: question.option_notes?.[i] ?? '',
        text: question.option_broadcasts?.[i] ?? '',
      }))
      : [{ ...BLANK_CHOICE }, { ...BLANK_CHOICE }],
    ask_when_key: question?.ask_when_caller_leaves ? CALLER_LEAVES : question?.ask_when_key ?? '',
    ask_when_values: question?.ask_when_values ?? [],
    raises_priority: question?.raises_priority ?? false,
    in_broadcast: question?.in_broadcast ?? false,
    in_details: question?.in_details ?? true,
    short_word: question?.short_word ?? '',
    expect_answer: question?.expect_answer ?? false,
    multiple: question?.multiple ?? false,
  }
}

function QuestionDialog({
  title, question, candidates, onSave, onClose,
}: {
  title: string
  /** Absent when this is a new one. */
  question?: CallQuestion
  /** The questions whose answers this one may hang off — earlier ones, with
   *  options to choose from. */
  candidates: readonly CallQuestion[]
  onSave: (body: QuestionInput) => Promise<void>
  onClose: () => void
}) {
  const start = useRef(draftOf(question))
  const [draft, setDraft] = useState<Draft>(start.current)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useReturnFocus()

  const dirty = JSON.stringify(draft) !== JSON.stringify(start.current)
  const { leave, backdrop } = useDialogGuard(
    () => (dirty && !busy
      ? 'Discard what you have typed? Everything already saved stays as it is.'
      : null),
    onClose,
  )

  const parent = candidates.find((c) => c.key === draft.ask_when_key) ?? null

  function set(change: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...change }))
  }

  /** One answer's row changed. EVERYTHING ABOUT AN ANSWER LIVES ON ITS ROW —
   *  its code piece, whether it is red, its note and its text — so renaming it
   *  keeps all of them with it, and removing it takes all of them away. */
  function setChoice(index: number, change: Partial<Choice>) {
    const choices = draft.choices.map(
      (c, i) => (i === index ? { ...c, ...change } : c))
    set({
      choices,
      // Nothing red left, nothing to raise the priority on.
      raises_priority: choices.some((c) => c.red) ? draft.raises_priority : false,
    })
  }

  function setOption(index: number, value: string) {
    setChoice(index, { option: value })
  }


  async function save() {
    setBusy(true)
    setError(null)
    const choice = draft.kind === 'choice'
    // A blank answer is dropped WITH its piece, from the same rows, before the
    // two lists are split apart -- dropping it from one list only is the slide
    // `Choice` exists to prevent.
    const kept = draft.choices
      .map((c) => ({ ...c, option: c.option.trim(), piece: c.piece.trim(),
                     note: c.note.trim(), text: c.text.trim() }))
      .filter((c) => c.option)
    try {
      // EVERY EDITABLE FIELD IS SENT, INCLUDING THE NULLS. The server reads
      // `model_fields_set`, so `alert_values: null` means "it no longer has a
      // safety answer" while a MISSING key means "leave it alone" — and this
      // dialog holds the whole question, so leaving one out would silently keep
      // a value the person just cleared.
      await onSave({
        prompt: draft.prompt.trim(),
        // Only on a create. A question cannot change between multiple-choice
        // and typed, and the server refuses `kind` on a patch with a sentence
        // rather than ignoring it.
        ...(question ? {} : { kind: draft.kind }),
        options: choice ? kept.map((c) => c.option) : null,
        // ALWAYS SENT BESIDE `options`: the server refuses changed options on
        // a question that has pieces unless the pieces come too. Null when no
        // answer adds anything, which is also how removing the last piece
        // clears them.
        option_codes: choice && kept.some((c) => c.piece)
          ? kept.map((c) => c.piece) : null,
        // ONE CONDITION OR THE OTHER (077), never both.
        ask_when_key: draft.ask_when_key && draft.ask_when_key !== CALLER_LEAVES
          ? draft.ask_when_key : null,
        ask_when_values: draft.ask_when_key && draft.ask_when_key !== CALLER_LEAVES
          ? draft.ask_when_values : null,
        ask_when_caller_leaves: draft.ask_when_key === CALLER_LEAVES,
        alert_values: choice && kept.some((c) => c.red)
          ? kept.filter((c) => c.red).map((c) => c.option) : null,
        raises_priority: choice && kept.some((c) => c.red) ? draft.raises_priority : false,
        // A vehicle may go out too: it is short parts, bounded on the server,
        // and it goes WITHOUT its plate -- where a typed answer is not bounded
        // at all.
        in_broadcast: choice || draft.kind === 'vehicle' ? draft.in_broadcast : false,
        // Every kind: a typed answer can go to the one member who accepted,
        // where it could never ride on a broadcast.
        in_details: draft.in_details,
        // THE SHORT WORD (084): empty is none -- the full question.
        short_word: draft.short_word.trim() || null,
        // Paired with `options` like the pieces, and sent beside them for the
        // same reason. The texts only when the whole-answer tick is off: the
        // server allows one rule per question.
        option_notes: choice && kept.some((c) => c.note)
          ? kept.map((c) => c.note) : null,
        option_broadcasts: choice && !draft.in_broadcast && kept.some((c) => c.text)
          ? kept.map((c) => c.text) : null,
        expect_answer: draft.expect_answer,
        multiple: choice ? draft.multiple : false,
      })
    } catch (e) {
      // THE DIALOG STAYS OPEN AND THE WORDS STAY IN THE BOXES. The server's own
      // sentence is better than anything this screen could say — "'Garage' is
      // not one of this question's options, so it could never be chosen" — and
      // it is only useful beside the box it is about.
      setError(e instanceof ApiError ? e.message : 'That could not be saved.')
      setBusy(false)
    }
  }

  return (
    <div className="modalback" {...backdrop}>
      <div className="modal wide" role="dialog" aria-modal="true"
           aria-labelledby="qdialog">
        <h3 id="qdialog">{title}</h3>

        {error && <div className="notice" role="alert">{error}</div>}

        <fieldset className="bare" disabled={busy}>
          <div className="field">
            <label htmlFor="qprompt">What the dispatcher is asked</label>
            <input
              id="qprompt"
              value={draft.prompt}
              maxLength={300}
              autoFocus
              placeholder="Which tire is flat?"
              onChange={(e) => set({ prompt: e.target.value })}
            />
          </div>

          {!question ? (
            <div className="field">
              <label>How it is answered</label>
              <div className="chips">
                <button type="button"
                  className={`chip ${draft.kind === 'choice' ? 'on' : ''}`}
                  onClick={() => set({ kind: 'choice' })}>
                  Pick one of a list
                </button>
                <button type="button"
                  className={`chip ${draft.kind === 'text' ? 'on' : ''}`}
                  onClick={() => set({
                    kind: 'text',
                    // A typed answer can carry neither a safety flag nor a
                    // place in the broadcast, and the server refuses both —
                    // deciding whether a typed sentence is the alarm comes down
                    // to how somebody spelled it, and an unbounded answer would
                    // push the nature and the location out of the first text
                    // segment. (The red ticks live on the answer rows, which
                    // a typed question does not send.)
                    raises_priority: false, in_broadcast: false,
                  })}>
                  Typed in
                </button>
                <button type="button"
                  className={`chip ${draft.kind === 'vehicle' ? 'on' : ''}`}
                  onClick={() => set({
                    kind: 'vehicle',
                    // A vehicle cannot be a safety answer — there is no one
                    // answer to flag — but unlike a typed one it may go in the
                    // broadcast, so that tick is left as it was.
                    raises_priority: false,
                  })}>
                  A vehicle
                </button>
              </div>
              {/* ONLY WHILE IT IS CHOSEN, because the chip's two words do not
                  say what the dispatcher will see, and this is the one kind
                  whose answer is not something typed on this dialog. */}
              {draft.kind === 'vehicle' && (
                <p className="footnote">
                  Make, model, year and colour, each a dropdown from the vehicle
                  list that also takes typing.
                </p>
              )}
            </div>
          ) : (
            <p className="footnote">
              {KIND_NAMES[draft.kind]} — and that cannot be changed. Its options,
              its safety answer and every condition naming one of them would be
              left pointing at nothing. Retire it and add the one you want.
            </p>
          )}

          {draft.kind === 'choice' && (
            <div className="field">
              <div className="qchoice">
                <label>The answers he can choose</label>
                <span className="lbl">Adds to code</span>
                <span className="lbl">Red</span>
              </div>
              {draft.choices.map((choice, i) => (
                <div className="qchoicerow" key={i}>
                  <div className="fieldrow qchoice">
                    <input
                      value={choice.option}
                      maxLength={80}
                      aria-label={`Option ${i + 1}`}
                      onChange={(e) => setOption(i, e.target.value)}
                    />
                    {/* Six at most, mirroring `call_questions.CODE_MAX`. */}
                    <input
                      className="qpiecein"
                      value={choice.piece}
                      maxLength={6}
                      aria-label={`Code piece for option ${i + 1}`}
                      autoComplete="off"
                      spellCheck={false}
                      onChange={(e) => setChoice(i, { piece: e.target.value.toUpperCase() })}
                    />
                    {/* RED ON THE ROW, not a list elsewhere naming answers by
                        their words: renaming "No" cannot leave a red "No"
                        pointing at nothing. More than one may be red. */}
                    <input
                      type="checkbox"
                      className="qredin"
                      checked={choice.red}
                      aria-label={`Option ${i + 1} is a safety flag`}
                      onChange={(e) => setChoice(i, { red: e.target.checked })}
                    />
                    <button
                      className="ghost tiny nogrow"
                      type="button"
                      aria-label={`Remove option ${i + 1}`}
                      onClick={() => set({
                        choices: draft.choices.filter((_, at) => at !== i),
                      })}
                    >
                      ×
                    </button>
                  </div>
                  <div className="fieldrow qchoicemore">
                    <input
                      value={choice.note}
                      maxLength={200}
                      aria-label={`Note for the responder when option ${i + 1} is chosen`}
                      placeholder="Note for the responder (optional)"
                      onChange={(e) => setChoice(i, { note: e.target.value })}
                    />
                    <input
                      value={choice.text}
                      maxLength={80}
                      disabled={draft.in_broadcast}
                      aria-label={`Text message line when option ${i + 1} is chosen`}
                      placeholder={draft.in_broadcast
                        ? 'Every answer is sent (below)'
                        : 'Line added to the text message (optional)'}
                      onChange={(e) => setChoice(i, { text: e.target.value })}
                    />
                  </div>
                </div>
              ))}
              <button className="ghost tiny nogrow" type="button"
                onClick={() => set({
                  choices: [...draft.choices, { ...BLANK_CHOICE }],
                })}>
                Add an answer
              </button>
            </div>
          )}

          {/* THE CONDITION, WHICH IS THE WHOLE FEATURE. It names an earlier
              QUESTION and never a nature — that is what makes a shared set
              shareable: Flat tire's "which side is the flat on" hangs off the
              SHARED street question, and Boost simply does not include the tire
              question. */}
          <div className="field">
            <label htmlFor="qwhen">Only ask this when…</label>
            <select
              id="qwhen"
              className="pick"
              value={draft.ask_when_key}
              onChange={(e) => set({
                // The values belong to the question that was chosen, so they go
                // when it does. Keeping them would send a condition reading
                // "when the vehicle is Front left".
                ask_when_key: e.target.value, ask_when_values: [],
              })}
            >
              <option value="">Always ask it</option>
              {/* THE CALL'S OWN BOX (077): "Is the caller staying on scene?"
                  -- for "Where will the keys be?". */}
              <option value={CALLER_LEAVES}>The caller is not staying on scene</option>
              {candidates
                .filter((c) => c.kind === 'choice' && (c.options ?? []).length > 0)
                .map((c) => (
                  <option key={c.key} value={c.key}>{c.prompt}</option>
                ))}
            </select>
            {parent && (
              <div className="checks" style={{ marginTop: 8 }}>
                {(parent.options ?? []).map((option) => (
                  <label className="check" key={option}>
                    <input
                      type="checkbox"
                      checked={draft.ask_when_values.includes(option)}
                      onChange={(e) => set({
                        ask_when_values: e.target.checked
                          ? [...draft.ask_when_values, option]
                          : draft.ask_when_values.filter((v) => v !== option),
                      })}
                    />
                    {option}
                  </label>
                ))}
              </div>
            )}
          </div>

          {draft.kind === 'choice' && draft.choices.some((c) => c.red) && (
            <label className="check">
              <input
                type="checkbox"
                checked={draft.raises_priority}
                onChange={(e) => set({ raises_priority: e.target.checked })}
              />
              a red answer takes the call to the top of the queue
            </label>
          )}

          {/* MORE THAN ONE ANSWER (076, the customer: "somebody stepped all
              his four tires"). Each press turns one answer on or off; the
              code is every picked piece in this list's order. */}
          {draft.kind === 'choice' && (
            <label className="check">
              <input
                type="checkbox"
                checked={draft.multiple}
                onChange={(e) => set({ multiple: e.target.checked })}
              />
              more than one answer can be picked (for “which tires are flat”)
            </label>
          )}

          {/* EXPECTED, NOT REQUIRED. No question may stop a call; this only
              makes a skipped one impossible to miss on the working call. */}
          <label className="check">
            <input
              type="checkbox"
              checked={draft.expect_answer}
              onChange={(e) => set({ expect_answer: e.target.checked })}
            />
            show “not confirmed” in red on the call while it is unanswered
          </label>

          {/* THE SECOND PART OF THE MESSAGE (057). Every answer has always
              gone to the member who accepts; this is the tick that keeps one
              back, for an organization that finds the details "too much
              information for the acceptor". Any kind, typed answers too. */}
          <label className="check">
            <input
              type="checkbox"
              checked={draft.in_details}
              onChange={(e) => set({ in_details: e.target.checked })}
            />
            send the answer to the member who accepts the call
          </label>
          {/* THE SHORT WORD (084): how the answer reads in the message to the
              member going, "Flat: Front left" -- and a blank, {Flat}, in the
              organization's own text on Settings -> Messages. */}
          {draft.in_details && (
            <div className="field qshort">
              <label htmlFor="qshortword">Short word in his message</label>
              <input id="qshortword" maxLength={20} value={draft.short_word}
                     placeholder="e.g. Flat"
                     onChange={(e) => set({ short_word: e.target.value.replace(/[[\]{}:]/g, '') })} />
              <p className="footnote">
                {draft.short_word.trim()
                  ? <>He reads “{draft.short_word.trim()}: {draft.choices.find((c) => c.option.trim())?.option.trim() || 'the answer'}”.
                      In your own message on Messages it is also the blank {`{${draft.short_word.trim()}}`}.</>
                  : 'Empty: the answer goes with its full question, as now.'}
              </p>
            </div>
          )}

          {/* CHOICE OR VEHICLE: the two kinds whose answer is bounded, which is
              the server's rule for what may ride on a text message. */}
          {(draft.kind === 'choice' || draft.kind === 'vehicle') && (
            <>
              <label className="check">
                <input
                  type="checkbox"
                  checked={draft.in_broadcast}
                  onChange={(e) => set({ in_broadcast: e.target.checked })}
                />
                {draft.kind === 'vehicle'
                  ? 'send the vehicle out with the broadcast (never the plate)'
                  : 'send every answer out with the broadcast'}
              </label>
              {/* THE ANSWERS' OWN LINES GO ON SAVE, because the server keeps one
                  rule per question. The boxes above only grey out, still
                  showing the wording, so without this the dialog says the
                  lines are kept and Save deletes them. Shown only while it is
                  true; unticking before saving keeps them. */}
              {draft.kind === 'choice' && draft.in_broadcast
                && draft.choices.some((c) => c.option.trim() && c.text.trim()) && (
                <p className="notice" role="status">
                  Saving with this ticked removes the answers’ own text lines.
                  Untick it to keep them.
                </p>
              )}
              {/* THIS SENTENCE POINTED AT THE WRONG TICK. Its last clause --
                  "the tick above it is what moves the call up the queue" --
                  sat directly under the BROADCAST tick, while the tick that
                  moves the queue is two controls up and is drawn only once a
                  safety answer has been chosen. So whenever none had been, the
                  broadcast tick was the only one above this sentence and the
                  sentence was simply false about it.

                  The cost of believing it: an administrator who wants the
                  queue behaviour ticks the control the words point at, and
                  every member's emergency text for that nature grows by the
                  prompt and the answer instead — to everybody, including the
                  people who do not take the call. Nothing downstream refuses
                  it; the first evidence is a member's phone. This tick is the
                  one human decision the whole opt-in rests on, so its label is
                  the only guard there is. */}
              <p className="footnote">
                The broadcast is nature, location and urgency — just enough to
                decide whether to take the call. Tick this only for an answer
                that changes whether somebody goes; it is sent to everyone, not
                only to whoever accepts.
              </p>
            </>
          )}

          {/* SAID ON BOTH, and it matters most on the edit. The internal name
              is what every recorded answer is filed under and what every
              condition points at, and it is made from the wording ONCE — so the
              question this screen exists to answer, "is it safe to reword
              this?", has to be answered where the rewording happens. */}
          <p className="footnote">
            {question
              ? 'Reword it freely. Its internal name was made from the original'
                + ' wording and never changes, so the conditions pointing at it'
                + ' keep working and the answers already recorded keep reading as'
                + ' they were asked.'
              : 'Its internal name is made from this wording when you add it, and'
                + ' then never changes — so rewording the question later is safe,'
                + ' and cannot orphan the conditions pointing at it or rewrite the'
                + ' answers already frozen on old jobs.'}
          </p>
        </fieldset>

        <div className="modalrow">
          <button className="ghost" type="button" onClick={leave}>Cancel</button>
          {/* NOT DISABLED ON ANYTHING THE SERVER DECIDES — an empty prompt is
              the only thing refused here, and a disabled button cannot deliver
              the server's sentence about a condition with no answers ticked or
              a set with one option. */}
          <button className="primary" type="button"
            disabled={busy || !draft.prompt.trim()} onClick={save}>
            {busy ? 'Saving…' : question ? 'Save' : 'Add it'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Trying it, without inventing a call
// ---------------------------------------------------------------------------

/** The dispatcher's own intake form, run against the tree being edited.
 *
 *  THE CUSTOMER ASKED FOR IT LOOKING AT THE FINISHED EDITOR: "I will add a
 *  third column on the right side that we should be able to test it right away.
 *  We shouldn't need to go back and do, uh, assuming as the call comes in, and
 *  we are asking the questions. When we click on one, you should be able to
 *  test it and see the whole flow if it works fine."
 *
 *  IT RUNS THE CODE THE REAL INTAKE RUNS, and that is the entire point rather
 *  than an implementation note. `QuestionFields` is the dispatcher's form and
 *  `visible()` is the browser's copy of `call_questions.visible` — the same two
 *  `Intake.tsx` and `CallAnswers.tsx` use. A rehearsal with a renderer of its
 *  own, or show-and-hide logic of its own, would drift from the form it claims
 *  to be showing; and a rehearsal that drifts is worse than none, because it
 *  reports that a tree works when the real form will not ask what he thinks.
 *  Nothing about a question is drawn here that is not drawn there.
 *
 *  IT WRITES NOTHING. The one request it can make is the broadcast preview,
 *  which composes a string and stores nothing — no incident, no answers, no row
 *  anywhere. The answers themselves live in this component until the column
 *  closes.
 *
 *  SO IT SAYS SO. A pane that draws intake exactly, on a screen reached from
 *  Settings, is one somebody could believe he has just dispatched from — the
 *  heading says so in three words, and there is a way to start over.
 */
function Rehearsal({ natureId, natureCode, tree }: {
  natureId: string
  /** The nature's own code, FT, which every call code on it ends in. */
  natureCode: string | null
  tree: readonly CallQuestion[]
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({})
  /** THE REHEARSAL'S OWN "Is the caller staying on scene?" (077), drawn only
   *  when something here is asked when he is not. */
  const [leaving, setLeaving] = useState(false)
  const asking = { callerLeaving: leaving }
  const asksWhenLeaving = tree.some((q) => q.ask_when_caller_leaves)
  const afterStay = underStaying(tree)

  /** The broadcast text, as the SERVER composed it. Never built here — see
   *  `BroadcastPreview` in `api.ts` for why a copy in the browser would be the
   *  second composer. */
  const [broadcast, setBroadcast] = useState<BroadcastPreview | null>(null)
  const [composing, setComposing] = useState(false)
  const [castError, setCastError] = useState<string | null>(null)

  /** The tree changed under the answers. WHAT "COULD INVALIDATE" MEANS, in
   *  full: the question is no longer asked at all (retired, removed, or its
   *  set taken off), it is no longer answered the same way, or the answer given
   *  is no longer one of the answers it offers — renaming an option leaves the
   *  old text sitting in this map, where no chip would show it as chosen and
   *  the ticket below would print it anyway. `prune` then drops whatever a
   *  changed condition or a reorder has stopped showing, which is the same
   *  function the working call uses for the same moment.
   *
   *  REWORDING A PROMPT, MOVING THE SAFETY ANSWER AND TICKING THE BROADCAST
   *  INVALIDATE NOTHING — they change what is drawn, not what was answered —
   *  so an edit to any of those redraws with the rehearsal intact, which is
   *  what makes it usable while he is working.
   *
   *  IT IS NOT `prune` WIDENED. The working call must NEVER drop a stored
   *  answer for being off today's option list: `call_questions.record` says so
   *  in as many words — the answer was valid when it was given and carries its
   *  own frozen prompt. Nothing here is stored, so the opposite is right. */
  useEffect(() => {
    setAnswers((held) => {
      const asks = new Map(tree.map((q) => [q.key, q]))
      const kept: Record<string, string> = {}
      for (const [key, value] of Object.entries(held)) {
        const question = asks.get(key)
        if (!question) continue
        if (question.kind === 'choice'
          && !(question.options ?? []).includes(value)) continue
        kept[key] = value
      }
      // KEEPS WHAT ONLY THE CALLER'S BOX HIDES (077), as Take a call does.
      return prune(tree, kept, { callerLeaving: true })
    })
  }, [tree])

  /** A COMPOSED TEXT IS ONLY EVER TRUE OF THE ANSWERS IT WAS COMPOSED FROM, so
   *  it goes the moment either the answers or the tree move under it. It is
   *  fetched on a press rather than on every change — one POST per chip while
   *  he clicks down a form is the wrong shape entirely — and the cost of that
   *  is exactly this: a preview left on screen after the next click would be
   *  the message for a call he is no longer describing, which is the drift this
   *  whole column exists to catch, wearing the composer's own authority.
   *
   *  Pressing the button changes neither, so what he asked for stays until he
   *  changes something.
   *
   *  IT CLEARS ON A TYPED ANSWER TOO, which can never reach a broadcast — the
   *  server refuses `in_broadcast` on anything but a choice or a vehicle. Narrowing
   *  this to the answers that CAN reach one would be this component deciding
   *  which answers are broadcast-worthy, which is the single judgement the
   *  preview route keeps to itself. */
  useEffect(() => {
    setBroadcast(null)
    setCastError(null)
    // AND ANY PRESS STILL IN FLIGHT IS ABANDONED WITH IT. Clearing only what
    // is on screen leaves a reply on its way that would put it straight back.
    attempt.current += 1
  }, [answers, tree, leaving])

  /** WHICH REQUEST THIS IS. The effect above clears the text whenever the
   *  answers move, but it cannot cancel a POST already in flight -- so
   *  pressing the button, then changing an answer before the reply lands, put
   *  the OLD text back on screen underneath the new answers. A message reading
   *  "child locked inside: Yes" beside a ticket saying No, with the button
   *  reading "Compose it again" so nothing suggested it was out of date.
   *
   *  That is the drift this whole column exists to catch, wearing the server's
   *  authority -- and the comment above asserts it cannot happen, so nobody
   *  would go looking. A counter is enough: a reply from a stale press is
   *  dropped rather than rendered. */
  const attempt = useRef(0)

  async function compose() {
    const mine = ++attempt.current
    setComposing(true)
    try {
      const made = await api.questionBroadcastPreview(natureId, answers, leaving)
      if (mine !== attempt.current) return
      setBroadcast(made)
      setCastError(null)
    } catch (e) {
      // NAMED ON SCREEN RATHER THAN SWALLOWED. A button that spins and then
      // does nothing is the failure CLAUDE.md records against the caller
      // history and the address save: a control drawn and then refused, with
      // no 403 anywhere for whoever investigates.
      if (mine !== attempt.current) return
      setBroadcast(null)
      setCastError(e instanceof ApiError
        ? e.message : 'Could not compose the broadcast text.')
    } finally {
      if (mine === attempt.current) setComposing(false)
    }
  }

  const stillToAsk = outstanding(tree, answers, asking)

  /** What the dispatcher would say on the air for these answers, by the same
   *  `callCode` the intake form draws it with. */
  const code = callCode(natureCode, tree, answers, asking)

  /** Pieces set on this tree while the nature itself has no code, which
   *  compose to no call code at all -- said, because otherwise the code simply
   *  never appears here and nothing says why. */
  const piecesWithoutCode = !natureCode
    && tree.some((q) => (q.option_codes ?? []).some(Boolean))

  /** The ticket this call would carry: every visible question that has an
   *  answer, in tree order.
   *
   *  THAT ORDER IS THE SERVER'S. `tree_for` resolves `sort_order` to the
   *  position in this list and `incident_answers.sort_order` stores it, so
   *  reading `visible()` top to bottom is the order a responder reads. */
  const ticket = visible(tree, answers, asking)
    .map((question) => ({ question, answer: (answers[question.key] ?? '').trim() }))
    .filter((line) => line.answer !== '')

  return (
    /* NAMED BY ITS OWN HEADING rather than by an `aria-label` repeating it.
       This is the second `<aside>` on the screen — the nature list is the
       other — and two unlabelled complementary landmarks are two identical
       entries in the landmark menu. */
    <aside className="qtry" aria-labelledby="qtryname">
      <div className="card">
        <h2 id="qtryname">
          Try it
          <span className="lbl">nothing is saved</span>
        </h2>

        <div className="qtryhead">
          {/* FIRST ON THE LINE AND REDRAWN ON EVERY PRESS, so checking that
              Front gives FFT is one click and one glance. */}
          {code && <span className="callcode big">{code}</span>}
          <span className={`pill ${stillToAsk.length ? '' : 'go'}`}>
            {stillToAsk.length === 0
              ? 'all answered'
              : `${stillToAsk.length} unanswered`}
          </span>
          <button
            className="ghost tiny"
            type="button"
            disabled={Object.keys(answers).length === 0}
            onClick={() => setAnswers({})}
          >
            Start over
          </button>
        </div>
        {piecesWithoutCode && (
          <p className="qnocode">
            No call code: these answers add pieces, but this nature has no code.
          </p>
        )}

        <QuestionFields
          tree={tree}
          answers={answers}
          alertNote={rehearsalAlert}
          onChange={setAnswers}
          asking={asking}
          keep={{ callerLeaving: true }}
          only={(q) => !afterStay.has(q.key)}
          // NO `onCommit`, which is what makes this write nothing: it is the
          // prop the working call saves on, and the intake form leaves it out
          // for the same reason -- there is nothing to save until a call
          // exists, and here there is never one.
        />

        {/* LAID OUT AS TAKE A CALL IS (077): the caller's box after the
            questions, and what it asks right under it. */}
        {asksWhenLeaving && (
          <>
            <div className="qtrycaller">
              <span className="lbl">Is the caller staying on scene?</span>
              <button type="button" className={`chip ${leaving ? '' : 'on'}`}
                      aria-pressed={!leaving} onClick={() => setLeaving(false)}>Staying</button>
              <button type="button" className={`chip alarm ${leaving ? 'on' : ''}`}
                      aria-pressed={leaving} onClick={() => setLeaving(true)}>Not staying</button>
            </div>
            {leaving && (
              <div className="qform qleaving">
                <QuestionFields
                  tree={tree}
                  answers={answers}
                  alertNote={rehearsalAlert}
                  onChange={setAnswers}
                  asking={asking}
                  keep={{ callerLeaving: true }}
                  only={(q) => afterStay.has(q.key)}
                />
              </div>
            )}
          </>
        )}

        {/* THE SAME FACT THE REAL FORM STATES, in the same words. A rehearsal
            that looked as though it required answers would teach him the
            opposite of what the product does: not one question can hold a
            dispatch up. */}
        {stillToAsk.length > 0 && (
          <p className="qwaiting">
            {stillToAsk.length} still unanswered — add them after the broadcast.
          </p>
        )}

        {/* WHAT THE JOB WOULD READ AS. The questions above are the flow; this
            is the OUTPUT he is testing, and seeing it is how he checks that
            the wording reads properly to somebody who has to act on it.
            `.qold` is the working call's own frozen-answer row, borrowed
            rather than redrawn, so the two cannot come to disagree about what
            a flagged line looks like. */}
        <div className="qticket">
          <h3 className="qsubhead">The ticket this would make</h3>
          {ticket.length === 0 ? (
            <p className="qempty">Nothing answered yet.</p>
          ) : ticket.map(({ question, answer }) => (
            <div
              className={`qold ${alerting(question, answer) ? 'flagged' : ''}`}
              key={question.key}
            >
              <span className="qoldq">{question.prompt}</span>
              {/* A VEHICLE AS ITS SENTENCE. The form holds it as JSON, and this
                  line printed that -- `{"make":"TOYOTA",...}` on the ticket
                  the column exists to show him. */}
              <span className="qolda">
                {question.kind === 'vehicle'
                  ? vehicleText(decodeVehicle(answer))
                  : answerText(question, answer)}
              </span>
              {/* WHICH OF THESE LEAVES THE BUILDING. The heading over this
                  block says "the ticket this would make", and a ticket is what
                  whoever ACCEPTS reads -- but a question ticked for the
                  broadcast also goes to everybody who did not. Four lines with
                  nothing to tell them apart leaves him no way to check the one
                  thing the tick exists for: that "Is a child locked inside?"
                  reaches people deciding whether to go.

                  THE BADGE AND THE TEXT BELOW ANSWER DIFFERENT QUESTIONS and
                  both earn their place. This says which of the answers in
                  front of him are ticked, on every row, with no request; the
                  block underneath says what the message those ticks produce
                  actually reads like, and how long it is. */}
              {/* THE SAVE'S RULE, mirrored: a vehicle goes without its plate, so
                  one given only a plate is not in the text at all, and the
                  badge says which half of a vehicle row leaves the building. */}
              {broadcastLine(question, answer) !== null && (
                <span className="lbl qoldcast">
                  {question.kind === 'vehicle'
                    ? 'in the broadcast, without the plate'
                    : 'in the broadcast'}
                </span>
              )}
              {noteFor(question, answer) && (
                <span className="qoldnote">{noteFor(question, answer)}</span>
              )}
            </div>
          ))}
        </div>

        {/* THE TEXT ITSELF, WHICH HE ASKED FOR: "add the button to see the
            broadcast text."

            IN THE PANE AND NOT IN A DIALOG. What he is checking is the text
            AGAINST the questions and the ticket above it — a dialog would
            cover the very rows he is comparing it with.

            AND COMPOSED BY THE SERVER, ON A PRESS. Every character of it comes
            from `build_broadcast_body`, the only place a broadcast is composed
            anywhere in this product; nothing here concatenates anything. The
            press is what pays for that: the answers change with every chip he
            clicks, and composing on each one would be a request per click. */}
        <div className="qcast">
          <h3 className="qsubhead">The broadcast text</h3>
          <button
            className="ghost tiny"
            type="button"
            disabled={composing}
            onClick={compose}
          >
            {composing
              ? 'Composing…'
              : broadcast ? 'Compose it again' : 'Show the broadcast text'}
          </button>

          {castError && <div className="notice" role="alert">{castError}</div>}

          {broadcast && (
            <>
              {/* `aria-live` SO IT IS ANNOUNCED WHEN IT ARRIVES. It appears
                  behind a press, several inches below the button, and a screen
                  reader would otherwise report nothing at all having happened. */}
              {/* THE WORDING ROUND IT, when the organization has set any
                  (migration 053). Drawn as its own line rather than pasted
                  into the text, so it is obvious which half is standing
                  wording set in Operations and which half is this call. */}
              {broadcast.header && (
                <p className="qcastwrap">{broadcast.header}</p>
              )}
              <p className="qcasttext" aria-live="polite">{broadcast.body}</p>
              {broadcast.footer && (
                <p className="qcastwrap">{broadcast.footer}</p>
              )}

              {/* THE LENGTH IS THE POINT OF THE WHOLE PANEL. The broadcast is
                  lean because it goes to everybody, by text, on every call —
                  so the number is what tells him a ticked question has pushed
                  the message into a second one. The 160 is a RULE OF THUMB and
                  is written as one: what a carrier actually does depends on
                  the alphabet the message ends up in, and a precise segment
                  count presented as fact would be this screen inventing a
                  certainty it does not have. */}
              <p className="qcastlen">
                <strong>{broadcast.characters} characters.</strong>{' '}
                A text message is about 160, so much past that it usually
                arrives as two.
                {(broadcast.header || broadcast.footer)
                  && ' That counts your header and footer, which every broadcast carries.'}
              </p>
              {/* THE TEXT SERVER CUTS AT 1600 AND SAYS NOTHING, and the
                  composer puts the answers LAST -- so what a cut removes is
                  precisely the ticked answers being read on this screen. A
                  prompt may be 300 characters and an option 80, so five wordy
                  ticked questions reach it, and the person checking his
                  wording here is the one person who would never find out that
                  the last of it does not arrive. Email carries it whole, which
                  makes the two channels disagree about what was sent. */}
              {broadcast.characters > 1600 && (
                <p className="notice" role="alert">
                  Past 1,600 characters the text server cuts the message, and
                  the answers are at the end — so the last of this would not
                  arrive by text. Email would carry all of it.
                </p>
              )}

              {/* A NATURE WITH NOTHING TICKED IS NOT A FAULT — it is what every
                  question starts as and what most of them stay. So this says
                  what the text IS rather than showing an empty box under a
                  heading, which reads as something having gone wrong. */}
              {broadcast.answers.length === 0 && (
                <p className="qcastnone">
                  None of these questions is ticked for the broadcast, so
                  this is the ordinary one — nature, location and urgency —
                  which is what your members get today.
                </p>
              )}

              {/* THE THREE INVENTED VALUES, NAMED. A message that looks exactly
                  like a real one, on a screen somebody could believe he has
                  dispatched from, has to say which parts of it are not real —
                  and they are printed from what the server was handed rather
                  than written out here, so the label cannot come to describe a
                  message nobody composed. */}
              <p className="qcastfake">
                Made up for this preview: <strong>{broadcast.stand_ins.location_label}</strong>
                {broadcast.stand_ins.urgency
                  ? <>, at {broadcast.stand_ins.urgency}.</>
                  : ', at ordinary urgency.'}
                {' '}A real call moves the count either way: its address is
                whatever the dispatcher types, and any urgency but the ordinary
                one adds a word in front.
              </p>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}

/** What a safety answer means, said as a rehearsal rather than as a call.
 *
 *  ITS OWN, LIKE EVERY OTHER CALLER'S. `alertNote` is a required prop precisely
 *  because the consequence differs per screen and has to be stated rather than
 *  implied — the intake form knows whether the dispatcher has chosen an urgency
 *  and can name the one the call will go out at; this has no urgency control
 *  and no call, so it says what a real one would do and does not pretend to
 *  know which branch it would take.
 *
 *  IT IS THE ONE ANSWER ON HIS ROSTER WHERE GETTING THE SET-UP WRONG HAS A COST
 *  WORTH NAMING — "Is a child locked inside?" — and checking it is most of why
 *  this column exists. Nothing here is hardcoded to that question: which answer
 *  is the alarm, and whether it moves the queue, are columns on the question.
 */
function rehearsalAlert(question: CallQuestion): string {
  return question.raises_priority
    ? 'Flagged — a real call turns red on the ticket and the queue, and goes to'
      + ' the top unless an urgency was chosen.'
    : 'Flagged — a real call turns red on the ticket and the queue.'
}

// ---------------------------------------------------------------------------
// His own three, to compare against
// ---------------------------------------------------------------------------

/** The customer's three worked examples, drawn by the SAME renderer as a live
 *  tree, so he can hold his own table up against what is actually set up.
 *
 *  CLOSED, AND LABELLED AS AN EXAMPLE. A fake tree that looks live on a screen
 *  where somebody is building the real one is worth being careful about: this
 *  is his message, not the organization's data, and nothing on it is a control.
 */
const EXAMPLES: Array<{ nature: string; questions: OutlineQuestion[] }> = [
  {
    nature: 'Flat tire',
    questions: [
      ex('which_tire_is_flat', 'Which tire is flat?',
        ['Front left', 'Front right', 'Rear left', 'Rear right']),
      ...VEHICLE_LOCATION(),
      ex('which_side_is_the_flat_on', 'Which side is the flat on?',
        ['Traffic side', 'Sidewalk side'],
        { when: 'parked_on_the_street', values: ['Parked on the side'] }),
      ex('is_there_room_to_work', 'Is there room to work?', ['Yes', 'No'],
        { when: 'where_is_the_vehicle', values: ['Garage'] }),
    ],
  },
  {
    nature: 'Boost / jump start',
    questions: [
      ...VEHICLE_LOCATION(),
      ex('which_way_does_the_hood_face', 'Which way does the hood face?',
        ['Towards the door', 'Towards the wall'],
        { when: 'where_is_the_vehicle', values: ['Garage'] }),
      ex('are_cables_needed', 'Are cables needed?', ['Yes', 'No']),
    ],
  },
  {
    nature: 'Car lockout',
    questions: [
      ex('is_a_child_locked_inside', 'Is a child locked inside?', ['Yes', 'No'],
        { alert: 'Yes', raises: true }),
      ex('is_the_engine_running', 'Is the engine running?', ['Yes', 'No']),
      ...VEHICLE_LOCATION(),
    ],
  },
]

/** The shared set, written once here exactly as it is written once in the
 *  database. Three natures point at it and none of them holds a copy. */
function VEHICLE_LOCATION(): OutlineQuestion[] {
  return [
    ex('where_is_the_vehicle', 'Where is the vehicle?',
      ['Parking lot', 'Street', 'Driveway', 'Garage']),
    ex('parked_on_the_street', 'Parked on the side, or in the roadway?',
      ['Parked on the side', 'In the roadway'],
      { when: 'where_is_the_vehicle', values: ['Street'] }),
    ex('what_kind_of_road', 'What kind of road?',
      ['Normal or busy street', 'Quiet or dirt road'],
      { when: 'parked_on_the_street', values: ['In the roadway'] }),
  ]
}

function ex(
  key: string, prompt: string, options: string[],
  extra?: { when?: string; values?: string[]; alert?: string; raises?: boolean },
): OutlineQuestion {
  return {
    key,
    prompt,
    kind: 'choice',
    options,
    ask_when_key: extra?.when ?? null,
    ask_when_values: extra?.values ?? null,
    alert_values: extra?.alert ? [extra.alert] : null,
    raises_priority: extra?.raises ?? false,
    in_broadcast: false,
  }
}

function WorkedExamples() {
  return (
    <div className="card qbuild">
      <h2>The three you wrote out</h2>
      <p className="blurb">
        Your own examples, drawn the way this screen draws a real one — not what
        is set up. Note that Boost asks the same location questions and does not
        ask which side the flat is on: that is the shared set doing its job.
      </p>
      <details open>
        <summary className="qsummary">Show them</summary>
        <div className="qexamples">
          {EXAMPLES.map((example) => {
            const depth = depths(example.questions)
            const promptFor = promptsByKey(example.questions)
            return (
              <div className="qexample" key={example.nature}>
                <h3 className="qexname">{example.nature}</h3>
                {example.questions.map((q) => (
                  <QuestionRow
                    key={q.key}
                    question={q}
                    depth={depth[q.key] ?? 0}
                    promptFor={promptFor}
                  />
                ))}
              </div>
            )
          })}
        </div>
      </details>
    </div>
  )
}
